import React, { useState, useEffect, useCallback, createContext, useContext } from 'react';
import './AdminPanel.css';
import ImageUpload from './ImageUpload';
import { getApiBaseUrl, loadAdminConfigFromServer } from '../config';

// ── Sub-components defined OUTSIDE AdminPanel so React never remounts them ──
// If they were defined inside, every parent re-render creates a new component
// type, React unmounts/remounts the input, and focus is lost after each keystroke.

const TextField = ({ label, name, rows, formData, onChange }) => (
  <div className="form-group">
    <label>{label}</label>
    {rows
      ? <textarea name={name} value={formData[name] ?? ''} onChange={onChange} rows={rows} />
      : <input type="text" name={name} value={formData[name] ?? ''} onChange={onChange} />
    }
  </div>
);

const Toggle = ({ label, name, formData, onChange }) => (
  <div className="checkbox-group">
    <label>
      <input type="checkbox" name={name} checked={!!formData[name]} onChange={onChange} />
      {label}
    </label>
  </div>
);

const BulletGroup = ({ label, prefix, count, formData, onChange }) => (
  <div className="admin-subsection">
    <div className="subsection-label">{label}</div>
    {Array.from({ length: count }, (_, i) => (
      <TextField key={i} label={`Item ${i + 1}`} name={`${prefix}${i + 1}`} formData={formData} onChange={onChange} />
    ))}
  </div>
);

// ────────────────────────────────────────────────────────────────────────────
// Context lets F/T/B be stable module-level components (no remount on each
// formData change, no focus loss after typing).
const FormCtx = createContext(null);
const F = (props) => { const { formData, onChange } = useContext(FormCtx); return <TextField   {...props} formData={formData} onChange={onChange} />; };
const T = (props) => { const { formData, onChange } = useContext(FormCtx); return <Toggle      {...props} formData={formData} onChange={onChange} />; };
const B = (props) => { const { formData, onChange } = useContext(FormCtx); return <BulletGroup {...props} formData={formData} onChange={onChange} />; };
// ────────────────────────────────────────────────────────────────────────────

function AdminPanel({ onClose, token }) {
  const [activePage, setActivePage] = useState('general');

  // Seed with empty strings — server config loads in the useEffect below.
  // We intentionally do NOT call useConfig() here: subscribing to the live
  // config context would cause re-renders on every WS push, remounting inputs
  // and losing focus mid-word.
  const [formData, setFormData] = useState({
    // ── BRANDING ──────────────────────────────────────────────
    prymeName: '',
    apexName:  '',

    // ── LANDING — Hero ────────────────────────────────────────
    heroTitle:    '',
    heroSubtitle: '',
    heroImage:         '',
    prymeHeroImage:    '', prymeComparisonImage: '', prymePipelineImage: '',
    apexHeroImage:     '', apexComparisonImage:  '', apexPipelineImage:  '',
    heroStat1Value: '0',        heroStat1Label: 'Training samples required',
    heroStat2Value: '<50ms',    heroStat2Label: 'Detection latency',
    heroStat3Value: 'Same day', heroStat3Label: 'Time to deploy',

    // ── LANDING — Problem ─────────────────────────────────────
    problemText: '',

    // ── LANDING — Product cards ───────────────────────────────
    prymeCardTagline: '',
    prymeCardFeature1: '', prymeCardFeature2: '', prymeCardFeature3: '',
    prymeCardFeature4: '', prymeCardFeature5: '',
    apexCardTagline: '',
    apexCardFeature1: '', apexCardFeature2: '', apexCardFeature3: '',
    apexCardFeature4: '', apexCardFeature5: '',

    // ── LANDING — Principles ──────────────────────────────────
    principle1Label: '', principle1Detail: '',
    principle2Label: '', principle2Detail: '',
    principle3Label: '', principle3Detail: '',

    // ── LANDING — Use cases ───────────────────────────────────
    ucLanding1Industry: '', ucLanding1Desc: '',
    ucLanding2Industry: '', ucLanding2Desc: '',
    ucLanding3Industry: '', ucLanding3Desc: '',
    ucLanding4Industry: '', ucLanding4Desc: '',
    ucLanding5Industry: '', ucLanding5Desc: '',
    ucLanding6Industry: '', ucLanding6Desc: '',

    // ── LANDING — Deployment cards ────────────────────────────
    deployOnDeviceTagline: '',
    deployOnDeviceFeature1: '', deployOnDeviceFeature2: '', deployOnDeviceFeature3: '',
    deployOnDeviceFeature4: '', deployOnDeviceFeature5: '',
    deployCloudTagline: '',
    deployCloudFeature1: '', deployCloudFeature2: '', deployCloudFeature3: '',
    deployCloudFeature4: '', deployCloudFeature5: '',

    // ── LANDING — Code section ────────────────────────────────
    codeSectionHeading: '',
    codeSectionSubheading: '',

    // ── LANDING — CTA ─────────────────────────────────────────
    landingCTACheck1: '', landingCTACheck2: '', landingCTACheck3: '',
    landingCTACheck4: '', landingCTACheck5: '',

    // ── LANDING — Section Visibility ──────────────────────────
    landingHeroEnabled: true, landingProblemEnabled: true, landingProductsEnabled: true,
    landingPrinciplesEnabled: true, landingUseCasesEnabled: true,
    landingDeploymentEnabled: true, landingCodeWindowEnabled: true, landingCtaEnabled: true,
    landingShowPlaceholders: true,

    // ── PRYME — Hero ──────────────────────────────────────────
    prymeHeroTitle: '', prymeHeroSubtitle: '',
    prymeHeroStat1Value: '', prymeHeroStat1Label: '',
    prymeHeroStat2Value: '', prymeHeroStat2Label: '',
    prymeHeroStat3Value: '', prymeHeroStat3Label: '',

    // ── PRYME — Pipeline ──────────────────────────────────────
    prymePipeline1Summary: '', prymePipeline2Summary: '', prymePipeline3Summary: '',
    prymePipeline4Summary: '', prymePipeline5Summary: '',

    // ── PRYME — Capabilities ──────────────────────────────────
    prymeCap1Title: '', prymeCap1Desc: '', prymeCap2Title: '', prymeCap2Desc: '',
    prymeCap3Title: '', prymeCap3Desc: '', prymeCap4Title: '', prymeCap4Desc: '',
    prymeCap5Title: '', prymeCap5Desc: '', prymeCap6Title: '', prymeCap6Desc: '',

    // ── PRYME — Advantages ────────────────────────────────────
    prymeAdv1Value: '', prymeAdv1Metric: '', prymeAdv1Desc: '',
    prymeAdv2Value: '', prymeAdv2Metric: '', prymeAdv2Desc: '',
    prymeAdv3Value: '', prymeAdv3Metric: '', prymeAdv3Desc: '',
    prymeAdv4Value: '', prymeAdv4Metric: '', prymeAdv4Desc: '',

    // ── PRYME — Use cases ─────────────────────────────────────
    prymeUC1Title: '', prymeUC1Desc: '', prymeUC1Feature1: '', prymeUC1Feature2: '', prymeUC1Feature3: '',
    prymeUC2Title: '', prymeUC2Desc: '', prymeUC2Feature1: '', prymeUC2Feature2: '', prymeUC2Feature3: '',
    prymeUC3Title: '', prymeUC3Desc: '', prymeUC3Feature1: '', prymeUC3Feature2: '', prymeUC3Feature3: '',
    prymeUC4Title: '', prymeUC4Desc: '', prymeUC4Feature1: '', prymeUC4Feature2: '', prymeUC4Feature3: '',

    // ── PRYME — CTA ───────────────────────────────────────────
    prymeCTABody: '',
    prymeCTACheck1: '', prymeCTACheck2: '', prymeCTACheck3: '',
    prymeCTACheck4: '', prymeCTACheck5: '',

    // ── PRYME — Section Visibility ────────────────────────────
    prymeShowPlaceholders: true,
    prymeHeroEnabled: true, prymePipelineEnabled: true, prymeCapabilitiesEnabled: true,
    prymeAdvantagesEnabled: true, prymeUseCasesEnabled: true,
    prymeComparisonEnabled: true, prymePrivacyEnabled: true,

    // ── APEX — Hero ───────────────────────────────────────────
    apexHeroTitle: '', apexHeroSubtitle: '',
    apexHeroStat1Value: '', apexHeroStat1Label: '',
    apexHeroStat2Value: '', apexHeroStat2Label: '',
    apexHeroStat3Value: '', apexHeroStat3Label: '',

    // ── APEX — Insight ────────────────────────────────────────
    apexInsightPara1: '', apexInsightPara2: '',

    // ── APEX — Pipeline ───────────────────────────────────────
    apexPipeline1Summary: '', apexPipeline2Summary: '', apexPipeline3Summary: '',
    apexPipeline4Summary: '', apexPipeline5Summary: '',

    // ── APEX — Mode comparison ────────────────────────────────
    apexModeNoSample1: '', apexModeNoSample2: '', apexModeNoSample3: '', apexModeNoSample4: '',
    apexModeCalib1: '',    apexModeCalib2: '',    apexModeCalib3: '',    apexModeCalib4: '',

    // ── APEX — Capabilities ───────────────────────────────────
    apexCap1Title: '', apexCap1Desc: '', apexCap2Title: '', apexCap2Desc: '',
    apexCap3Title: '', apexCap3Desc: '', apexCap4Title: '', apexCap4Desc: '',
    apexCap5Title: '', apexCap5Desc: '', apexCap6Title: '', apexCap6Desc: '',

    // ── APEX — Advantages ─────────────────────────────────────
    apexAdv1Value: '', apexAdv1Metric: '', apexAdv1Desc: '',
    apexAdv2Value: '', apexAdv2Metric: '', apexAdv2Desc: '',
    apexAdv3Value: '', apexAdv3Metric: '', apexAdv3Desc: '',
    apexAdv4Value: '', apexAdv4Metric: '', apexAdv4Desc: '',

    // ── APEX — Use cases ──────────────────────────────────────
    apexUC1Title: '', apexUC1Desc: '', apexUC1Feature1: '', apexUC1Feature2: '', apexUC1Feature3: '',
    apexUC2Title: '', apexUC2Desc: '', apexUC2Feature1: '', apexUC2Feature2: '', apexUC2Feature3: '',
    apexUC3Title: '', apexUC3Desc: '', apexUC3Feature1: '', apexUC3Feature2: '', apexUC3Feature3: '',
    apexUC4Title: '', apexUC4Desc: '', apexUC4Feature1: '', apexUC4Feature2: '', apexUC4Feature3: '',

    // ── APEX — CTA ────────────────────────────────────────────
    apexCTABody: '',
    apexCTACheck1: '', apexCTACheck2: '', apexCTACheck3: '',
    apexCTACheck4: '', apexCTACheck5: '',

    // ── APEX — Section Visibility ─────────────────────────────
    apexShowPlaceholders: true,
    apexHeroEnabled: true, apexInsightEnabled: true, apexPipelineEnabled: true,
    apexCapabilitiesEnabled: true, apexAdvantagesEnabled: true, apexUseCasesEnabled: true,
    apexComparisonEnabled: true, apexPrivacyEnabled: true,

    // ── ON-DEVICE — Section toggles ─────────────────────────
    odShowPlaceholders: true,
    odHeroImage: '', odPiImage: '',
    odHeroEnabled: true, odPiEnabled: true, odPlatformsEnabled: true,
    odModesEnabled: true, odPrivacyEnabled: true, odComingSoonEnabled: true, odCTAEnabled: true,

    // ── ON-DEVICE — Hero ──────────────────────────────────────
    odHeroTitle: 'Voice Intelligence.\nRuns on the Hardware.\nStays on the Hardware.',
    odHeroSubtitle: 'The complete Tytonvoice pipeline — VAD, keyword detection, and transcription — runs fully on-device with no network dependency. From Raspberry Pi to Android to production Linux hardware.',
    odHeroStat1Value: '0',     odHeroStat1Label: 'Network calls at runtime',
    odHeroStat2Value: '<50ms', odHeroStat2Label: 'Detection latency on Pi 4',
    odHeroStat3Value: '4',     odHeroStat3Label: 'Platforms available now',

    // ── ON-DEVICE — Raspberry Pi showcase ────────────────────
    odPiDesc: 'Pryme keyword detection running fully on-chip on a Raspberry Pi 4. No cloud connection. No GPU. Sub-50ms detection latency from spoken word to triggered event — on a $35 single-board computer.',
    odPiSpec1: 'Raspberry Pi 4 (2GB RAM)', odPiSpec2: 'CPU-only inference',
    odPiSpec3: 'Full offline — no network', odPiSpec4: 'Live microphone input',
    odPiSpec5: 'Pryme multi-keyword detection',

    // ── ON-DEVICE — Platforms ─────────────────────────────────
    odPlat1Name: 'Raspberry Pi', odPlat1Desc: 'The complete Tytonvoice pipeline — VAD, detection, and transcription — runs entirely on-chip. No network required. Proven in live demos with Pryme keyword detection at under 50ms latency on Pi 4 hardware.',
    odPlat1Spec1: 'Tested on Raspberry Pi 4 (2GB+)', odPlat1Spec2: 'CPU-only inference, no GPU required',
    odPlat1Spec3: 'Under 50ms end-to-end detection latency', odPlat1Spec4: 'Full offline operation — zero network dependency',
    odPlat1Spec5: 'Both Pryme and Apex SDK supported',
    odPlat2Name: 'Android', odPlat2Desc: 'Native Android SDK built in Kotlin. Run models fully on-device with OFFLINE=true for maximum privacy, or connect to your cloud backend for larger model variants. Supports both Pryme and Apex.',
    odPlat2Spec1: 'Native Kotlin SDK — Android Studio integration', odPlat2Spec2: 'OFFLINE=true for fully local inference',
    odPlat2Spec3: 'Hybrid mode for cloud-backed larger models', odPlat2Spec4: 'Supports ARMv8 and x86_64 architectures',
    odPlat2Spec5: 'Background service and foreground mic support',
    odPlat3Name: 'Windows', odPlat3Desc: 'Python SDK runs locally on any Windows machine with OFFLINE=true — no network, no cloud dependency. Full pipeline on CPU. Cloud mode available for teams wanting centralized model management.',
    odPlat3Spec1: 'Python SDK — pip install tytonvoice', odPlat3Spec2: 'OFFLINE=true for fully local operation',
    odPlat3Spec3: 'CPU-optimized — runs on standard hardware', odPlat3Spec4: 'Windows 10 and 11 supported',
    odPlat3Spec5: 'Live mic and audio file input',
    odPlat4Name: 'Linux', odPlat4Desc: 'The primary development and deployment platform for Tytonvoice. Full SDK support across distributions. Ideal for server-side edge nodes, industrial hardware, and air-gapped environments.',
    odPlat4Spec1: 'Python SDK — pip install tytonvoice', odPlat4Spec2: 'OFFLINE=true for air-gapped deployments',
    odPlat4Spec3: 'Tested on Ubuntu, Debian, and Raspberry Pi OS', odPlat4Spec4: 'x86_64 and ARM64 architectures',
    odPlat4Spec5: 'Systemd service integration supported',

    // ── ON-DEVICE — Privacy points ────────────────────────────
    odPrivacy1Title: 'Audio Never Leaves the Device',
    odPrivacy1Detail: 'With OFFLINE=true, every stage of the pipeline — voice activity detection, transcription, and keyword detection — runs locally. No audio is transmitted, buffered remotely, or logged externally.',
    odPrivacy2Title: 'Air-Gap Capable',
    odPrivacy2Detail: 'On-device deployment requires no internet connection at runtime. Models are bundled or pre-loaded at deploy time. Suitable for classified, healthcare, and industrial environments.',
    odPrivacy3Title: 'No Third-Party Dependencies at Runtime',
    odPrivacy3Detail: 'Once deployed, the SDK has no external API calls, no telemetry, and no cloud fallback unless explicitly configured. You control the full execution environment.',
    odPrivacy4Title: 'You Own the Model Weights',
    odPrivacy4Detail: 'Model weights are distributed with the SDK and remain on your hardware. No model-as-a-service dependency, no per-inference billing, no vendor lock-in.',

    // ── ON-DEVICE — CTA ───────────────────────────────────────
    odCTABody: 'From a $35 Raspberry Pi to enterprise Android deployments — the same SDK, the same accuracy, fully offline.',
    odCTACheck1: 'Fully offline with OFFLINE=true', odCTACheck2: 'Audio never leaves the device',
    odCTACheck3: 'Raspberry Pi, Android, Windows, Linux', odCTACheck4: 'Same SDK API as cloud deployment',
    odCTACheck5: 'iOS, macOS, and C++ coming soon',

    // ── CLOUD — Section toggles ───────────────────────────────
    clHeroEnabled: true, clArchEnabled: true, clApiFeaturesEnabled: true,
    clPlatformsEnabled: true, clInfraEnabled: true, clComparisonEnabled: true, clCTAEnabled: true,

    // ── CLOUD — Hero ──────────────────────────────────────────
    clHeroTitle: 'Voice Intelligence.\nYour Infrastructure.\nYour Data.',
    clHeroSubtitle: 'Deploy the Tytonvoice backend on any cloud provider or server. Clients send audio — your infrastructure runs inference. No audio ever reaches Tytonvoice servers.',
    clHeroStat1Value: 'Any',    clHeroStat1Label: 'Cloud provider or VPS',
    clHeroStat2Value: 'Zero',   clHeroStat2Label: 'Audio to Tytonvoice servers',
    clHeroStat3Value: 'Same',   clHeroStat3Label: 'API as on-device SDK',

    // ── CLOUD — Platforms ─────────────────────────────────────
    clPlat1Name: 'Web App', clPlat1Desc: 'Browser-based clients stream audio to your backend via the REST API. No SDK installation required on the client side — works in any modern browser with microphone access.',
    clPlat1Spec1: 'REST API — fetch or axios from any JS framework', clPlat1Spec2: 'Live microphone streaming via MediaRecorder',
    clPlat1Spec3: 'Audio file upload support', clPlat1Spec4: 'CORS configurable per deployment',
    clPlat1Spec5: 'Works in Chrome, Firefox, Safari, Edge',
    clPlat2Name: 'Android', clPlat2Desc: 'The Kotlin SDK connects to your cloud backend when OFFLINE is not set. Larger model variants run server-side — the client handles audio capture and VAD on-device, inference happens in the cloud.',
    clPlat2Spec1: 'Native Kotlin SDK — cloud mode out of the box', clPlat2Spec2: 'On-device VAD — only speech reaches the backend',
    clPlat2Spec3: 'Switchable to offline with OFFLINE=true', clPlat2Spec4: 'Supports ARMv8 and x86_64 architectures',
    clPlat2Spec5: 'Background service and foreground mic support',
    clPlat3Name: 'Windows', clPlat3Desc: 'Python SDK defaults to cloud-backed inference when OFFLINE is not set. Centralized model management — update models on your backend without touching client deployments.',
    clPlat3Spec1: 'Python SDK — pip install tytonvoice', clPlat3Spec2: 'Cloud mode for larger, higher-accuracy models',
    clPlat3Spec3: 'Switchable to offline with OFFLINE=true', clPlat3Spec4: 'Windows 10 and 11 supported',
    clPlat3Spec5: 'Live mic and audio file input',
    clPlat4Name: 'Linux', clPlat4Desc: 'The primary server and edge-node platform. Run the backend itself on Linux, or deploy the client SDK in cloud mode pointing at a separate inference server.',
    clPlat4Spec1: 'Python SDK — pip install tytonvoice', clPlat4Spec2: 'Host the backend on the same or separate server',
    clPlat4Spec3: 'Tested on Ubuntu, Debian, and RHEL', clPlat4Spec4: 'x86_64 and ARM64 architectures',
    clPlat4Spec5: 'Systemd service integration supported',

    // ── CLOUD — Infrastructure points ────────────────────────
    clInfra1Title: 'Your Infrastructure', clInfra1Detail: 'The backend is a standard Flask/SocketIO application. Deploy it on any cloud provider, VPS, or on-premise server. No Tytonvoice-managed infrastructure is required or involved.',
    clInfra2Title: 'Your Data Stays with You', clInfra2Detail: 'Audio is sent from client to your backend — never to Tytonvoice servers. You control the network path, the storage policy, and the retention rules.',
    clInfra3Title: 'Horizontally Scalable', clInfra3Detail: 'Stateless inference endpoints can be load-balanced across multiple instances. Model weights load once per process — no per-request initialization cost after warmup.',
    clInfra4Title: 'Centralized Model Management', clInfra4Detail: 'Update keyword vocabularies, detection thresholds, and model variants on the backend without touching client deployments. All clients inherit changes instantly.',

    // ── CLOUD — CTA ───────────────────────────────────────────
    clCTABody: 'Your backend, your data, your network path. Deploy the Tytonvoice API on any server or cloud provider and connect any client platform in minutes.',
    clCTACheck1: 'Deploy on any cloud provider or VPS', clCTACheck2: 'Audio never reaches Tytonvoice servers',
    clCTACheck3: 'Web, Android, Windows, and Linux clients', clCTACheck4: 'REST API + live WebSocket config',
    clCTACheck5: 'Same API as on-device — one codebase',

    // ── GENERAL ───────────────────────────────────────────────
    footerEnabled:  true,
    navbarLogoPath: '',
  });

  const [hasChanges, setHasChanges]   = useState(false);
  const [saveMessage, setSaveMessage] = useState('');
  const [loading, setLoading]         = useState(true);

  // Load canonical config from server once on mount
  useEffect(() => {
    if (!token) return;
    loadAdminConfigFromServer(token).then(serverConfig => {
      if (serverConfig && Object.keys(serverConfig).length > 0) {
        setFormData(prev => ({ ...prev, ...serverConfig }));
      }
      setLoading(false);
    });
  }, [token]);

  const handleInputChange = useCallback((e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
    setHasChanges(true);
    setSaveMessage('');
  }, []);

  const handleImageUpload = (fieldName, imageUrl) => {
    setFormData(prev => ({ ...prev, [fieldName]: imageUrl }));
    setHasChanges(true);
    setSaveMessage('');
  };

  const handleSave = async () => {
    if (!token) { setSaveMessage('✗ Admin token not available'); return; }
    try {
      const response = await fetch(`${getApiBaseUrl()}/api/admin/config`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ config: formData }),
      });
      const data = await response.json();
      if (response.ok && data.success) {
        setSaveMessage('✓ Saved — changes are live site-wide');
        setHasChanges(false);
      } else if (response.status === 401) {
        setSaveMessage('✗ Session expired — please log in again');
      } else {
        setSaveMessage('✗ ' + (data.error || 'Error saving configuration'));
      }
    } catch (e) {
      setSaveMessage('✗ Error saving configuration');
      console.error('Error saving config:', e);
    }
  };



  if (loading) {
    return (
      <div className="admin-panel-overlay">
        <div className="admin-panel" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <p style={{ color: 'var(--text-light)' }}>Loading configuration...</p>
        </div>
      </div>
    );
  }

  return (
    <FormCtx.Provider value={{ formData, onChange: handleInputChange }}>
    <div className="admin-panel-overlay">
      <div className="admin-panel">
        <div className="admin-header">
          <h1>Content Management</h1>
          <button className="admin-close" onClick={onClose}>✕</button>
        </div>

        <div className="admin-body">
          <aside className="admin-sidebar">
            <nav className="admin-nav">
              {[
                { id: 'general',  label: 'General' },
              { id: 'landing',  label: 'Landing' },
              { id: 'pryme',    label: 'Pryme' },
              { id: 'apex',     label: 'Apex' },
              { id: 'ondevice', label: 'On-Device' },
              { id: 'cloud',    label: 'Cloud' },
              ].map(({ id, label }) => (
                <button
                  key={id}
                  className={`admin-nav-item ${activePage === id ? 'active' : ''}`}
                  onClick={() => setActivePage(id)}
                >
                  {label}
                </button>
              ))}
            </nav>
          </aside>

          <main className="admin-main">
            <div className="admin-content">
              <form className="admin-form" onSubmit={e => e.preventDefault()}>

                {/* ══ LANDING ══ */}
                {activePage === 'landing' && (<>

                  <section className="admin-section">
                    <div className="section-label">Section Visibility</div>
                    <T label="Hero"              name="landingHeroEnabled" />
                    <T label="Problem Statement" name="landingProblemEnabled" />
                    <T label="Product Cards"     name="landingProductsEnabled" />
                    <T label="Shared Principles" name="landingPrinciplesEnabled" />
                    <T label="Use Cases"         name="landingUseCasesEnabled" />
                    <T label="Deployment Cards"  name="landingDeploymentEnabled" />
                    <T label="Code Window"       name="landingCodeWindowEnabled" />
                    <T label="CTA"               name="landingCtaEnabled" />
                    <T label="Show Visual Placeholders" name="landingShowPlaceholders" />
                  </section>

                  <section className="admin-section">
                    <div className="section-label">Hero</div>
                    <F label="Title"    name="heroTitle" />
                    <F label="Subtitle" name="heroSubtitle" rows={3} />
                    <ImageUpload label="Hero Visual" currentImage={formData.heroImage} onImageUpload={handleImageUpload} fieldName="heroImage" token={token} />
                    <div className="admin-subsection">
                      <div className="subsection-label">Stats</div>
                      {[1,2,3].map(n => (
                        <div key={n} className="admin-stat-row">
                          <F label={`Stat ${n} Value`} name={`heroStat${n}Value`} />
                          <F label={`Stat ${n} Label`} name={`heroStat${n}Label`} />
                        </div>
                      ))}
                    </div>
                  </section>

                  <section className="admin-section">
                    <div className="section-label">Problem Statement</div>
                    <F label="Text" name="problemText" rows={3} />
                  </section>

                  <section className="admin-section">
                    <div className="section-label">Pryme Card</div>
                    <F label="Tagline" name="prymeCardTagline" rows={2} />
                    <B label="Feature Bullets" prefix="prymeCardFeature" count={5} />
                  </section>

                  <section className="admin-section">
                    <div className="section-label">Apex Card</div>
                    <F label="Tagline" name="apexCardTagline" rows={2} />
                    <B label="Feature Bullets" prefix="apexCardFeature" count={5} />
                  </section>

                  <section className="admin-section">
                    <div className="section-label">Shared Principles</div>
                    {[1,2,3].map(n => (
                      <div key={n} className="admin-subsection">
                        <div className="subsection-label">Principle {n}</div>
                        <F label="Label"  name={`principle${n}Label`} />
                        <F label="Detail" name={`principle${n}Detail`} rows={2} />
                      </div>
                    ))}
                  </section>

                  <section className="admin-section">
                    <div className="section-label">Use Cases</div>
                    {[1,2,3,4,5,6].map(n => (
                      <div key={n} className="admin-subsection">
                        <div className="subsection-label">Use Case {n}</div>
                        <F label="Industry"    name={`ucLanding${n}Industry`} />
                        <F label="Description" name={`ucLanding${n}Desc`} rows={2} />
                      </div>
                    ))}
                  </section>

                  <section className="admin-section">
                    <div className="section-label">On-Device Card</div>
                    <F label="Tagline" name="deployOnDeviceTagline" rows={2} />
                    <B label="Feature Bullets" prefix="deployOnDeviceFeature" count={5} />
                  </section>

                  <section className="admin-section">
                    <div className="section-label">Cloud Card</div>
                    <F label="Tagline" name="deployCloudTagline" rows={2} />
                    <B label="Feature Bullets" prefix="deployCloudFeature" count={5} />
                  </section>

                  <section className="admin-section">
                    <div className="section-label">Code Section</div>
                    <F label="Heading"    name="codeSectionHeading" />
                    <F label="Subheading" name="codeSectionSubheading" rows={2} />
                  </section>

                  <section className="admin-section">
                    <div className="section-label">CTA Checklist</div>
                    <B label="Checklist Items" prefix="landingCTACheck" count={5} />
                  </section>

                </>)}

                {/* ══ PRYME ══ */}
                {activePage === 'pryme' && (<>

                  <section className="admin-section">
                    <div className="section-label">Section Visibility</div>
                    <T label="Show Visual Placeholders" name="prymeShowPlaceholders" />
                    <T label="Hero"         name="prymeHeroEnabled" />
                    <T label="Pipeline"     name="prymePipelineEnabled" />
                    <T label="Capabilities" name="prymeCapabilitiesEnabled" />
                    <T label="Advantages"   name="prymeAdvantagesEnabled" />
                    <T label="Use Cases"    name="prymeUseCasesEnabled" />
                    <T label="Comparison"   name="prymeComparisonEnabled" />
                    <T label="Privacy"      name="prymePrivacyEnabled" />
                  </section>

                  <section className="admin-section">
                    <div className="section-label">Hero</div>
                    <F label="Title"    name="prymeHeroTitle" rows={2} />
                    <F label="Subtitle" name="prymeHeroSubtitle" rows={3} />
                    <ImageUpload label="Hero Visual" currentImage={formData.prymeHeroImage} onImageUpload={handleImageUpload} fieldName="prymeHeroImage" token={token} />
                    <div className="admin-subsection">
                      <div className="subsection-label">Stats</div>
                      {[1,2,3].map(n => (
                        <div key={n} className="admin-stat-row">
                          <F label={`Stat ${n} Value`} name={`prymeHeroStat${n}Value`} />
                          <F label={`Stat ${n} Label`} name={`prymeHeroStat${n}Label`} />
                        </div>
                      ))}
                    </div>
                  </section>

                  <section className="admin-section">
                    <div className="section-label">Pipeline Step Summaries</div>
                    {[1,2,3,4,5].map(n => (
                      <div key={n} className="admin-subsection">
                        <div className="subsection-label">Step {n}</div>
                        <F label="Summary" name={`prymePipeline${n}Summary`} rows={2} />
                      </div>
                    ))}
                  </section>

                  <section className="admin-section">
                    <div className="section-label">Capabilities</div>
                    {[1,2,3,4,5,6].map(n => (
                      <div key={n} className="admin-subsection">
                        <div className="subsection-label">Capability {n}</div>
                        <F label="Title"       name={`prymeCap${n}Title`} />
                        <F label="Description" name={`prymeCap${n}Desc`} rows={2} />
                      </div>
                    ))}
                  </section>

                  <section className="admin-section">
                    <div className="section-label">Advantages</div>
                    {[1,2,3,4].map(n => (
                      <div key={n} className="admin-subsection">
                        <div className="subsection-label">Advantage {n}</div>
                        <div className="admin-stat-row">
                          <F label="Value"  name={`prymeAdv${n}Value`} />
                          <F label="Metric" name={`prymeAdv${n}Metric`} />
                        </div>
                        <F label="Description" name={`prymeAdv${n}Desc`} rows={2} />
                      </div>
                    ))}
                  </section>

                  <section className="admin-section">
                    <div className="section-label">Use Cases</div>
                    {[1,2,3,4].map(n => (
                      <div key={n} className="admin-subsection">
                        <div className="subsection-label">Use Case {n}</div>
                        <F label="Title"       name={`prymeUC${n}Title`} />
                        <F label="Description" name={`prymeUC${n}Desc`} rows={2} />
                        <div className="admin-stat-row" style={{ gridTemplateColumns: '1fr 1fr 1fr' }}>
                          <F label="Tag 1" name={`prymeUC${n}Feature1`} />
                          <F label="Tag 2" name={`prymeUC${n}Feature2`} />
                          <F label="Tag 3" name={`prymeUC${n}Feature3`} />
                        </div>
                      </div>
                    ))}
                  </section>

                  <section className="admin-section">
                    <div className="section-label">Visuals</div>
                    <ImageUpload label="Comparison Graphic (Traditional vs Pryme)" currentImage={formData.prymeComparisonImage} onImageUpload={handleImageUpload} fieldName="prymeComparisonImage" token={token} />
                    <ImageUpload label="Pipeline Diagram" currentImage={formData.prymePipelineImage} onImageUpload={handleImageUpload} fieldName="prymePipelineImage" token={token} />
                  </section>

                  <section className="admin-section">
                    <div className="section-label">CTA</div>
                    <F label="Body text" name="prymeCTABody" rows={2} />
                    <B label="Checklist Items" prefix="prymeCTACheck" count={5} />
                  </section>

                </>)}

                {/* ══ APEX ══ */}
                {activePage === 'apex' && (<>

                  <section className="admin-section">
                    <div className="section-label">Section Visibility</div>
                    <T label="Show Visual Placeholders" name="apexShowPlaceholders" />
                    <T label="Hero"         name="apexHeroEnabled" />
                    <T label="Core Insight" name="apexInsightEnabled" />
                    <T label="Pipeline"     name="apexPipelineEnabled" />
                    <T label="Capabilities" name="apexCapabilitiesEnabled" />
                    <T label="Advantages"   name="apexAdvantagesEnabled" />
                    <T label="Use Cases"    name="apexUseCasesEnabled" />
                    <T label="Comparison"   name="apexComparisonEnabled" />
                    <T label="Privacy"      name="apexPrivacyEnabled" />
                  </section>

                  <section className="admin-section">
                    <div className="section-label">Hero</div>
                    <F label="Title"    name="apexHeroTitle" rows={2} />
                    <F label="Subtitle" name="apexHeroSubtitle" rows={3} />
                    <ImageUpload label="Hero Visual" currentImage={formData.apexHeroImage} onImageUpload={handleImageUpload} fieldName="apexHeroImage" token={token} />
                    <div className="admin-subsection">
                      <div className="subsection-label">Stats</div>
                      {[1,2,3].map(n => (
                        <div key={n} className="admin-stat-row">
                          <F label={`Stat ${n} Value`} name={`apexHeroStat${n}Value`} />
                          <F label={`Stat ${n} Label`} name={`apexHeroStat${n}Label`} />
                        </div>
                      ))}
                    </div>
                  </section>

                  <section className="admin-section">
                    <div className="section-label">Core Insight</div>
                    <F label="Paragraph 1" name="apexInsightPara1" rows={4} />
                    <F label="Paragraph 2" name="apexInsightPara2" rows={4} />
                  </section>

                  <section className="admin-section">
                    <div className="section-label">Pipeline Step Summaries</div>
                    {[1,2,3,4,5].map(n => (
                      <div key={n} className="admin-subsection">
                        <div className="subsection-label">Step {n}</div>
                        <F label="Summary" name={`apexPipeline${n}Summary`} rows={2} />
                      </div>
                    ))}
                  </section>

                  <section className="admin-section">
                    <div className="section-label">Pryme vs Apex — No Samples Card</div>
                    <B label="Feature Bullets" prefix="apexModeNoSample" count={4} />
                  </section>

                  <section className="admin-section">
                    <div className="section-label">Pryme vs Apex — Calibrated Card</div>
                    <B label="Feature Bullets" prefix="apexModeCalib" count={4} />
                  </section>

                  <section className="admin-section">
                    <div className="section-label">Capabilities</div>
                    {[1,2,3,4,5,6].map(n => (
                      <div key={n} className="admin-subsection">
                        <div className="subsection-label">Capability {n}</div>
                        <F label="Title"       name={`apexCap${n}Title`} />
                        <F label="Description" name={`apexCap${n}Desc`} rows={2} />
                      </div>
                    ))}
                  </section>

                  <section className="admin-section">
                    <div className="section-label">Advantages</div>
                    {[1,2,3,4].map(n => (
                      <div key={n} className="admin-subsection">
                        <div className="subsection-label">Advantage {n}</div>
                        <div className="admin-stat-row">
                          <F label="Value"  name={`apexAdv${n}Value`} />
                          <F label="Metric" name={`apexAdv${n}Metric`} />
                        </div>
                        <F label="Description" name={`apexAdv${n}Desc`} rows={2} />
                      </div>
                    ))}
                  </section>

                  <section className="admin-section">
                    <div className="section-label">Use Cases</div>
                    {[1,2,3,4].map(n => (
                      <div key={n} className="admin-subsection">
                        <div className="subsection-label">Use Case {n}</div>
                        <F label="Title"       name={`apexUC${n}Title`} />
                        <F label="Description" name={`apexUC${n}Desc`} rows={2} />
                        <div className="admin-stat-row" style={{ gridTemplateColumns: '1fr 1fr 1fr' }}>
                          <F label="Tag 1" name={`apexUC${n}Feature1`} />
                          <F label="Tag 2" name={`apexUC${n}Feature2`} />
                          <F label="Tag 3" name={`apexUC${n}Feature3`} />
                        </div>
                      </div>
                    ))}
                  </section>

                  <section className="admin-section">
                    <div className="section-label">Visuals</div>
                    <ImageUpload label="Comparison Graphic (Pryme vs Apex)" currentImage={formData.apexComparisonImage} onImageUpload={handleImageUpload} fieldName="apexComparisonImage" token={token} />
                    <ImageUpload label="Pipeline Diagram" currentImage={formData.apexPipelineImage} onImageUpload={handleImageUpload} fieldName="apexPipelineImage" token={token} />
                  </section>

                  <section className="admin-section">
                    <div className="section-label">CTA</div>
                    <F label="Body text" name="apexCTABody" rows={2} />
                    <B label="Checklist Items" prefix="apexCTACheck" count={5} />
                  </section>

                </>)}

                {/* ══ ON-DEVICE ══ */}
                {activePage === 'ondevice' && (<>

                  {/* Section visibility toggles */}
                  <section className="admin-section">
                    <div className="section-label">Section Visibility</div>
                    <T label="Show Visual Placeholders" name="odShowPlaceholders" />
                    <T label="Hero"                name="odHeroEnabled" />
                    <T label="Raspberry Pi Showcase" name="odPiEnabled" />
                    <T label="Platform Cards"      name="odPlatformsEnabled" />
                    <T label="Offline / Hybrid Modes" name="odModesEnabled" />
                    <T label="Privacy Architecture" name="odPrivacyEnabled" />
                    <T label="Coming Soon"         name="odComingSoonEnabled" />
                    <T label="CTA"                 name="odCTAEnabled" />
                  </section>

                  <section className="admin-section">
                    <div className="section-label">Hero</div>
                    <F label="Title (use \n for line breaks)" name="odHeroTitle" rows={3} />
                    <F label="Subtitle" name="odHeroSubtitle" rows={3} />
                    <ImageUpload label="Hero Visual" currentImage={formData.odHeroImage} onImageUpload={handleImageUpload} fieldName="odHeroImage" token={token} />
                    <div className="admin-subsection">
                      <div className="subsection-label">Stats</div>
                      {[1,2,3].map(n => (
                        <div key={n} className="admin-stat-row">
                          <F label={`Stat ${n} Value`} name={`odHeroStat${n}Value`} />
                          <F label={`Stat ${n} Label`} name={`odHeroStat${n}Label`} />
                        </div>
                      ))}
                    </div>
                  </section>

                  <section className="admin-section">
                    <div className="section-label">Raspberry Pi Showcase</div>
                    <F label="Description" name="odPiDesc" rows={3} />
                    <ImageUpload label="Pi Demo Image" currentImage={formData.odPiImage} onImageUpload={handleImageUpload} fieldName="odPiImage" token={token} />
                    <div className="admin-subsection">
                      <div className="subsection-label">Spec List</div>
                      {[1,2,3,4,5].map(n => (
                        <F key={n} label={`Spec ${n}`} name={`odPiSpec${n}`} />
                      ))}
                    </div>
                  </section>

                  <section className="admin-section">
                    <div className="section-label">Platform Cards</div>
                    {[1,2,3,4].map(n => (
                      <div key={n} className="admin-subsection">
                        <div className="subsection-label">Platform {n}</div>
                        <F label="Name"        name={`odPlat${n}Name`} />
                        <F label="Description" name={`odPlat${n}Desc`} rows={2} />
                        <div className="subsection-label" style={{ marginTop: '0.5rem', fontSize: '0.72rem' }}>Specs</div>
                        {[1,2,3,4,5].map(s => (
                          <F key={s} label={`Spec ${s}`} name={`odPlat${n}Spec${s}`} />
                        ))}
                      </div>
                    ))}
                  </section>

                  <section className="admin-section">
                    <div className="section-label">Privacy Architecture</div>
                    {[1,2,3,4].map(n => (
                      <div key={n} className="admin-subsection">
                        <div className="subsection-label">Point {n}</div>
                        <F label="Title"  name={`odPrivacy${n}Title`} />
                        <F label="Detail" name={`odPrivacy${n}Detail`} rows={2} />
                      </div>
                    ))}
                  </section>

                  <section className="admin-section">
                    <div className="section-label">CTA</div>
                    <F label="Body text" name="odCTABody" rows={2} />
                    <B label="Checklist Items" prefix="odCTACheck" count={5} />
                  </section>

                </>)}

                {/* ══ CLOUD ══ */}
                {activePage === 'cloud' && (<>

                  {/* Section visibility toggles */}
                  <section className="admin-section">
                    <div className="section-label">Section Visibility</div>
                    <T label="Hero"                  name="clHeroEnabled" />
                    <T label="Architecture Diagram"  name="clArchEnabled" />
                    <T label="API Features"          name="clApiFeaturesEnabled" />
                    <T label="Platform Cards"        name="clPlatformsEnabled" />
                    <T label="Infrastructure Points" name="clInfraEnabled" />
                    <T label="Cloud vs On-Device Comparison" name="clComparisonEnabled" />
                    <T label="CTA"                   name="clCTAEnabled" />
                  </section>

                  <section className="admin-section">
                    <div className="section-label">Hero</div>
                    <F label="Title (use \n for line breaks)" name="clHeroTitle" rows={3} />
                    <F label="Subtitle" name="clHeroSubtitle" rows={3} />
                    <div className="admin-subsection">
                      <div className="subsection-label">Stats</div>
                      {[1,2,3].map(n => (
                        <div key={n} className="admin-stat-row">
                          <F label={`Stat ${n} Value`} name={`clHeroStat${n}Value`} />
                          <F label={`Stat ${n} Label`} name={`clHeroStat${n}Label`} />
                        </div>
                      ))}
                    </div>
                  </section>

                  <section className="admin-section">
                    <div className="section-label">Platform Cards</div>
                    {[1,2,3,4].map(n => (
                      <div key={n} className="admin-subsection">
                        <div className="subsection-label">Platform {n}</div>
                        <F label="Name"        name={`clPlat${n}Name`} />
                        <F label="Description" name={`clPlat${n}Desc`} rows={2} />
                        <div className="subsection-label" style={{ marginTop: '0.5rem', fontSize: '0.72rem' }}>Specs</div>
                        {[1,2,3,4,5].map(s => (
                          <F key={s} label={`Spec ${s}`} name={`clPlat${n}Spec${s}`} />
                        ))}
                      </div>
                    ))}
                  </section>

                  <section className="admin-section">
                    <div className="section-label">Infrastructure Points</div>
                    {[1,2,3,4].map(n => (
                      <div key={n} className="admin-subsection">
                        <div className="subsection-label">Point {n}</div>
                        <F label="Title"  name={`clInfra${n}Title`} />
                        <F label="Detail" name={`clInfra${n}Detail`} rows={2} />
                      </div>
                    ))}
                  </section>

                  <section className="admin-section">
                    <div className="section-label">CTA</div>
                    <F label="Body text" name="clCTABody" rows={2} />
                    <B label="Checklist Items" prefix="clCTACheck" count={5} />
                  </section>

                </>)}

                {/* ══ GENERAL ══ */}                {/* ══ GENERAL ══ */}
                {activePage === 'general' && (<>

                  <section className="admin-section">
                    <div className="section-label">Display</div>
                    <T label="Show Footer" name="footerEnabled" />
                  </section>

                  <section className="admin-section">
                    <div className="section-label">Product Names</div>
                    <F label="Pryme — Product Name" name="prymeName" />
                    <F label="Apex — Product Name"  name="apexName" />
                  </section>

                  <section className="admin-section">
                    <div className="section-label">Navbar</div>
                    <F label="Logo path" name="navbarLogoPath" />
                  </section>

                </>)}

              </form>
            </div>
          </main>
        </div>

        <div className="admin-footer">
          {saveMessage && (
            <div className={`save-message ${saveMessage.includes('✓') ? 'success' : 'error'}`}>
              {saveMessage}
            </div>
          )}
          <div className="admin-buttons">
            <button className="btn-close" onClick={onClose} type="button">Close</button>
            <button
              className={`btn-save ${hasChanges ? 'active' : ''}`}
              onClick={handleSave}
              disabled={!hasChanges}
              type="button"
            >
              {hasChanges ? 'Save Changes' : 'No Changes'}
            </button>
          </div>
        </div>
      </div>
    </div>
    </FormCtx.Provider>
  );
}

export default AdminPanel;